import React, { useState, useEffect, useLayoutEffect, useMemo, useContext, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { Award, Star, ArrowUpDown, SlidersHorizontal, Trophy, Globe, Users, Lock, ExternalLink, RefreshCw, Check, Trash2, MoreVertical, Eye, Pencil, MessageCircle } from "lucide-react";
import Select from "react-select";
import { ResponsiveCard, useResponsive } from "./responsive";
import FeedFilterPanel, {
  ActiveFilterChips,
  DEFAULT_FILTERS,
  countActiveSections,
  dateFromForPreset,
} from "./FeedFilterPanel";
import recognitionTypesService from "../services/recognitionTypesService";
import recognitionsService from "../services/recognitionsService";
import nominationsService from "../services/nominationsService";
import reactionsService from "../services/reactionsService";
import commentsService from "../services/commentsService";
import ReactionBar from "./ReactionBar";
import CommentSection from "./CommentSection";
import teamsService from "../services/teamsService";
import workvivoService from "../services/workvivoService";
import OrgThemeContext from "../context/OrgThemeContext";
import AuthContext from "../context/AuthContext";
import ImportantDatesRail from "./ImportantDatesRail";
import UserAvatar from "./UserAvatar";

// Visibility badge helpers
const VISIBILITY_META = {
  org: { label: "Everyone", Icon: Globe },
  team: { label: "Team", Icon: Users },
  private: { label: "Private", Icon: Lock },
};

// Floating comment toggle shown on the bottom-right of feed cards. Mirrors the
// reactions/points pills; clicking it expands the CommentSection panel below.
const CommentToggle = ({ count = 0, open, onClick, accentColor = "#111827" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-expanded={open}
    aria-label={count > 0 ? `${count} comment${count === 1 ? "" : "s"}` : "Comment"}
    className="inline-flex items-center gap-1.5 min-h-[30px] px-2.5 py-1 rounded-full border-2 bg-white text-sm shadow-md transition-colors select-none"
    style={
      open
        ? { borderColor: accentColor, color: accentColor }
        : { borderColor: "#d1d5db", color: "#4b5563" }
    }
  >
    <MessageCircle className="w-4 h-4" />
    {count > 0 && (
      <span className="text-xs font-semibold tabular-nums leading-none">{count}</span>
    )}
  </button>
);

// Collapse an open panel when the user clicks/taps outside `ref`. Clicks inside
// the reactions emoji picker (portaled to <body>) are ignored, so opening the
// emoji picker for a comment never collapses the thread it belongs to.
const useClickOutsideClose = (ref, active, onClose) => {
  useEffect(() => {
    if (!active) return undefined;
    const handler = (e) => {
      const el = ref.current;
      if (!el || el.contains(e.target)) return;
      if (e.target.closest && e.target.closest(".reaction-picker-root")) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, active, onClose]);
};

// Staged dismissal for a card's comment thread: if the viewer has a comment
// editor open, the first outside click only collapses that editor, so a stray
// click never buries the thread they were mid-way through writing in. The next
// outside click closes the thread itself.
const useCommentPanelDismiss = (ref, open, setOpen) => {
  const [closeEditorsToken, setCloseEditorsToken] = useState(0);
  const editorActiveRef = useRef(false);

  const onEditorActiveChange = useCallback((active) => {
    editorActiveRef.current = active;
  }, []);

  const onClose = useCallback(() => {
    if (editorActiveRef.current) {
      // Clear eagerly so a quick second click closes the thread without waiting
      // for the panel's effect to report the editor shut.
      editorActiveRef.current = false;
      setCloseEditorsToken((t) => t + 1);
      return;
    }
    setOpen(false);
  }, [setOpen]);

  useClickOutsideClose(ref, open, onClose);
  return { closeEditorsToken, onEditorActiveChange };
};

// First name for the feed's greeting, used when an org hasn't set a welcome
// title of its own. Falls back to the email, then a neutral "there".
const greetingName = (user) => {
  const full = (user?.full_name || "").trim();
  if (full) return full.split(/\s+/)[0];
  return user?.email || "there";
};

// Helper to extract hex color from stored format
const extractHexColor = (colorString) => {
  if (!colorString) return null;
  const match = colorString.match(/\[(.*?)\]/);
  return match ? match[1] : null;
};

const formatRelativeTimestampParts = (timestamp) => {
  if (!timestamp) return null;
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return null;

  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((startOfToday - startOfDay) / 86400000);
  const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });

  let dayLabel;
  if (diffDays <= 0) dayLabel = "Today";
  else if (diffDays === 1) dayLabel = "1 day ago";
  else if (diffDays < 7) dayLabel = `${diffDays} days ago`;
  else if (diffDays < 14) dayLabel = "1 week ago";
  else {
    const opts = { month: "short", day: "numeric" };
    if (d.getFullYear() !== now.getFullYear()) opts.year = "numeric";
    dayLabel = d.toLocaleDateString("en-US", opts);
  }
  return { dayLabel, time };
};

const HeaderMetaSep = () => (
  <span className="text-white/50 mx-1.5 select-none" aria-hidden="true">·</span>
);

const MAX_VISIBLE_RECIPIENTS = 3;
const MOBILE_MAX_WIDTH_MQ = "(max-width: 639px)";

const recipientDisplayName = (r) =>
  r?.full_name || r?.name || r?.email || "Unknown User";

const RecipientOverflowMenu = ({ people, anchorEl, onClose, typeColor, title = "Also recognized" }) => {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);
  const isMobile =
    typeof window !== "undefined" && window.matchMedia(MOBILE_MAX_WIDTH_MQ).matches;

  useLayoutEffect(() => {
    if (isMobile) return undefined;
    const menu = menuRef.current;
    if (!menu || !anchorEl) return undefined;

    const place = () => {
      const a = anchorEl.getBoundingClientRect();
      const margin = 8;
      const menuWidth = menu.offsetWidth || 260;
      const menuHeight = menu.offsetHeight || 240;
      let left = a.left + a.width / 2 - menuWidth / 2;
      left = Math.min(Math.max(left, margin), window.innerWidth - menuWidth - margin);
      let top = a.bottom + 6;
      if (top + menuHeight > window.innerHeight - margin) {
        top = Math.max(margin, a.top - menuHeight - 6);
      }
      setPos({ left, top });
    };

    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      onClose();
    };

    place();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onClose);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onClose);
    };
  }, [anchorEl, people, onClose, isMobile]);

  useEffect(() => {
    const handlePointer = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        anchorEl &&
        !anchorEl.contains(e.target)
      ) {
        onClose();
      }
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (!isMobile) {
      document.addEventListener("mousedown", handlePointer);
      document.addEventListener("touchstart", handlePointer);
    }
    document.addEventListener("keydown", handleKey);
    const prevOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("touchstart", handlePointer);
      document.removeEventListener("keydown", handleKey);
      if (isMobile) document.body.style.overflow = prevOverflow;
    };
  }, [anchorEl, onClose, isMobile]);

  const listItems = (
    <ul className="py-1">
      {people.map((r) => {
        const name = recipientDisplayName(r);
        return (
          <li key={r.id} role="none">
            <Link
              role="menuitem"
              to={`/profile/${r.id}`}
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-3 sm:px-3 sm:py-2.5 hover:bg-gray-50 active:bg-gray-100 transition-colors"
            >
              <UserAvatar
                src={r.avatar_url}
                name={name}
                className="w-10 h-10 sm:w-8 sm:h-8 rounded-full object-cover flex-shrink-0"
                style={{ border: `2px solid ${typeColor || "#9ca3af"}` }}
                textClassName="text-sm sm:text-xs"
              />
              <span className="text-base sm:text-sm text-gray-800 truncate">{name}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );

  if (isMobile) {
    return createPortal(
      <div
        className="fixed inset-0 z-[200] flex flex-col justify-end menu-backdrop-in"
        style={{ background: "rgba(0,0,0,0.45)" }}
        onClick={onClose}
        role="presentation"
      >
        <div
          ref={menuRef}
          role="menu"
          aria-label={title}
          className="bg-white rounded-t-3xl shadow-2xl w-full max-h-[80vh] h-[min(80vh,560px)] flex flex-col menu-sheet-in"
          style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}
          onClick={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="flex justify-center pt-2 pb-1 flex-shrink-0">
            <div className="w-10 h-1 rounded-full bg-gray-300" aria-hidden="true" />
          </div>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between flex-shrink-0">
            <div>
              <p className="text-sm font-semibold text-gray-900">{title}</p>
              <p className="text-xs text-gray-400">{people.length} {people.length === 1 ? "person" : "people"}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 active:bg-gray-200"
            >
              Done
            </button>
          </div>
          <div
            className="overflow-y-auto touch-scroll flex-1 min-h-0 overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {listItems}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={title}
      className="fixed z-[200] w-64 max-h-72 flex flex-col bg-white border border-gray-200 rounded-2xl shadow-xl menu-pop-in overflow-hidden"
      style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
    >
      <div className="px-3 py-2 border-b border-gray-100 text-[11px] font-semibold uppercase tracking-wide text-gray-400 flex-shrink-0 bg-white">
        {title}
      </div>
      <div className="overflow-y-auto touch-scroll flex-1 min-h-0 overscroll-contain">
        {listItems}
      </div>
    </div>,
    document.body
  );
};

const RecipientAvatarCluster = ({ recipients, typeColor }) => {
  const valid = (recipients || []).filter((r) => r && r.id);
  const [menuOpen, setMenuOpen] = useState(false);
  const overflowBtnRef = useRef(null);

  if (valid.length === 0) return null;

  if (valid.length === 1) {
    const r = valid[0];
    const name = recipientDisplayName(r);
    return (
      <Link
        to={`/profile/${r.id}`}
        className="group block transition-transform duration-200 hover:scale-105"
      >
        <UserAvatar
          src={r.avatar_url}
          name={name}
          className="w-14 h-14 sm:w-24 sm:h-24 rounded-full object-cover transition-shadow duration-200 shadow-md group-hover:shadow-2xl"
          style={{ border: `3px solid ${typeColor}` }}
          textClassName="text-lg sm:text-2xl"
        />
      </Link>
    );
  }

  const visible = valid.slice(0, MAX_VISIBLE_RECIPIENTS);
  const overflowPeople = valid.slice(MAX_VISIBLE_RECIPIENTS);
  const overflow = overflowPeople.length;

  return (
    <div className="flex items-center justify-center -space-x-3 sm:-space-x-5">
      {visible.map((r, idx) => {
        const name = recipientDisplayName(r);
        return (
          <Link
            to={`/profile/${r.id}`}
            key={r.id}
            className="group relative transition-transform duration-150 hover:scale-110 hover:z-20"
            style={{ zIndex: visible.length - idx + 1 }}
            title={name}
          >
            <UserAvatar
              src={r.avatar_url}
              name={name}
              className="w-12 h-12 sm:w-20 sm:h-20 rounded-full object-cover bg-white transition-shadow duration-200 shadow-md group-hover:shadow-2xl"
              style={{ border: `3px solid ${typeColor}` }}
              textClassName="text-xs sm:text-xl"
            />
          </Link>
        );
      })}
      {overflow > 0 && (
        <>
          <button
            type="button"
            ref={overflowBtnRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="relative w-12 h-12 sm:w-20 sm:h-20 rounded-full flex items-center justify-center text-white text-xs sm:text-lg font-bold bg-gray-700 shadow-md hover:bg-gray-600 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-gray-400"
            style={{
              border: `3px solid ${typeColor}`,
              zIndex: 0,
            }}
            title={`${overflow} more recipient${overflow === 1 ? "" : "s"}`}
            aria-label={`Show ${overflow} more recognized ${overflow === 1 ? "person" : "people"}`}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            +{overflow}
          </button>
          {menuOpen && (
            <RecipientOverflowMenu
              people={overflowPeople}
              anchorEl={overflowBtnRef.current}
              onClose={() => setMenuOpen(false)}
              typeColor={typeColor}
            />
          )}
        </>
      )}
    </div>
  );
};

const MAX_NAME_LINES_MOBILE = 3;

const RecipientNames = ({ recipients, typeColor }) => {
  const valid = useMemo(
    () => (recipients || []).filter((r) => r && r.id),
    [recipients]
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const moreBtnRef = useRef(null);
  const containerRef = useRef(null);
  const lastWidthRef = useRef(0);

  const [visibleCount, setVisibleCount] = useState(() =>
    Math.min(MAX_VISIBLE_RECIPIENTS, Math.max(valid.length, 0))
  );

  const recipientKey = valid.map((r) => r.id).join(",");
  useEffect(() => {
    setVisibleCount(Math.min(MAX_VISIBLE_RECIPIENTS, valid.length));
  }, [recipientKey, valid.length]);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MAX_WIDTH_MQ);
    const onChange = () => {
      setVisibleCount(Math.min(MAX_VISIBLE_RECIPIENTS, valid.length));
    };
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, [valid.length]);

  useLayoutEffect(() => {
    if (valid.length === 0) return undefined;

    const el = containerRef.current;
    if (!el) return undefined;

    const isMobile = window.matchMedia(MOBILE_MAX_WIDTH_MQ).matches;
    if (!isMobile) {
      const desktopCap = Math.min(MAX_VISIBLE_RECIPIENTS, valid.length);
      if (visibleCount !== desktopCap) setVisibleCount(desktopCap);
      return undefined;
    }

    const styles = window.getComputedStyle(el);
    let lineHeight = parseFloat(styles.lineHeight);
    if (!lineHeight || Number.isNaN(lineHeight)) {
      lineHeight = parseFloat(styles.fontSize) * 1.25;
    }
    const lineCount = el.scrollHeight / lineHeight;
    if (lineCount > MAX_NAME_LINES_MOBILE + 0.2 && visibleCount > 1) {
      setVisibleCount((c) => Math.max(1, c - 1));
    }
    return undefined;
  }, [valid.length, visibleCount, recipientKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;

    lastWidthRef.current = el.offsetWidth;
    const ro = new ResizeObserver(() => {
      const width = el.offsetWidth;
      if (Math.abs(width - lastWidthRef.current) < 1) return;
      lastWidthRef.current = width;
      setVisibleCount(Math.min(MAX_VISIBLE_RECIPIENTS, valid.length));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [valid.length, recipientKey]);

  if (valid.length === 0) return null;

  const cap = Math.min(visibleCount, MAX_VISIBLE_RECIPIENTS, valid.length);
  const visible = valid.slice(0, cap);
  const overflowPeople = valid.slice(cap);
  const hasOverflow = overflowPeople.length > 0;

  return (
    <span ref={containerRef}>
      {visible.map((r, i) => {
        const name = recipientDisplayName(r);
        let separator = null;
        if (i > 0) {
          const isLastVisible = i === visible.length - 1;
          separator =
            !hasOverflow && isLastVisible ? (
              <span className="text-gray-400 mx-1 font-normal">and</span>
            ) : (
              <span className="text-gray-400 font-normal">, </span>
            );
        }
        return (
          <React.Fragment key={r.id}>
            {separator}
            <Link to={`/profile/${r.id}`} className="hover:underline">
              {name}
            </Link>
          </React.Fragment>
        );
      })}
      {hasOverflow && (
        <>
          {" "}
          <button
            type="button"
            ref={moreBtnRef}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            className="inline font-semibold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded-sm align-baseline"
            style={{ color: typeColor || "#4b5563" }}
            aria-label={`Show ${overflowPeople.length} more recognized ${overflowPeople.length === 1 ? "person" : "people"}`}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            +{overflowPeople.length} more
          </button>
          {menuOpen && (
            <RecipientOverflowMenu
              people={overflowPeople}
              anchorEl={moreBtnRef.current}
              onClose={() => setMenuOpen(false)}
              typeColor={typeColor}
            />
          )}
        </>
      )}
    </span>
  );
};

// Default fallback recognition type configurations
const DEFAULT_TYPE_CONFIG = {
  "Collaboration": { icon: "🤝", colorFrom: "#3b82f6", colorTo: "#06b6d4" },
  "Customer Service": { icon: "💼", colorFrom: "#22c55e", colorTo: "#10b981" },
  "Innovation": { icon: "💡", colorFrom: "#eab308", colorTo: "#f97316" },
  "Leadership": { icon: "👑", colorFrom: "#a855f7", colorTo: "#ec4899" },
  "Teamwork": { icon: "🎯", colorFrom: "#6366f1", colorTo: "#3b82f6" },
  "Excellence": { icon: "⭐", colorFrom: "#f59e0b", colorTo: "#eab308" },
};

export default function CoreFeed() {
  const orgTheme = useContext(OrgThemeContext);
  const { user: currentUser } = useContext(AuthContext);
  const { isMobile } = useResponsive();
  const [recognitions, setRecognitions] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [recognitionTypesData, setRecognitionTypesData] = useState([]);
  // Center the feed on the viewport (the actual page), not just within the
  // <main> area that the fixed sidebar offsets. We shift the feed rightward by
  // however much is needed to put its center at window-center, clamped so it
  // never overflows its column into the Important Dates rail. A ResizeObserver
  // keeps this correct as the sidebar expands/collapses and the rail loads in.
  const feedColRef = useRef(null);
  const [feedShift, setFeedShift] = useState(0);
  useEffect(() => {
    const el = feedColRef.current;
    if (!el) return;
    const FEED_MAX_WIDTH = 896; // matches max-w-4xl
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0) return;
      const feedWidth = Math.min(FEED_MAX_WIDTH, rect.width);
      const ideal = window.innerWidth / 2 - rect.left - feedWidth / 2;
      const maxShift = rect.width - feedWidth;
      setFeedShift(Math.max(0, Math.min(ideal, maxShift)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);
  const [teams, setTeams] = useState([]);
  const [adminTeamIds, setAdminTeamIds] = useState([]);
  // "<target_type>:<id>" -> [{key, emoji, count, reacted}]
  const [reactionsMap, setReactionsMap] = useState({});
  // "<target_type>:<id>" -> comment count
  const [commentCountsMap, setCommentCountsMap] = useState({});

  const activeFilterCount = countActiveSections(filters);

  // Sort option labels
  const sortLabels = {
    newest: 'Newest First',
    oldest: 'Oldest First',
    highest_points: 'Highest Points',
  };

  // Close the sort dropdown when clicking outside it
  useEffect(() => {
    if (!sortOpen) return;
    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [sortOpen]);

  // Build dynamic recognition type config from API data
  const recognitionTypeConfig = useMemo(() => {
    const config = { ...DEFAULT_TYPE_CONFIG };

    // Add API types (will override defaults if same key)
    recognitionTypesData.forEach((type) => {
      config[type.value] = {
        icon: type.icon,
        colorFrom: extractHexColor(type.color_from) || "#a855f7",
        colorTo: extractHexColor(type.color_to) || "#3b82f6",
      };
    });

    return config;
  }, [recognitionTypesData]);

  // Fetch recognition types from API
  const fetchRecognitionTypes = async () => {
    try {
      const types = await recognitionTypesService.getMyRecognitionTypes();
      if (types && types.length > 0) {
        setRecognitionTypesData(types);
      }
    } catch (err) {
      console.error("Failed to fetch recognition types:", err);
      // Keep using defaults on error
    }
  };

  // Only recognitions can match these sections, so nominations are excluded
  // from the feed while any of them is active (AND across sections).
  const hasRecognitionOnlyFilters = (f) =>
    f.typeIds.length > 0 || f.teamIds.length > 0 || f.points.length === 1;

  const buildRecognitionsFeedUrl = (apiUrl, sortBy, f) => {
    const params = new URLSearchParams({ sort_by: sortBy });
    f.typeIds.forEach((id) => params.append('recognition_type_ids', id));
    f.teamIds.forEach((id) => params.append('team_ids', id));
    f.users.forEach((u) => params.append('user_ids', u.value));
    f.involvement.forEach((v) => params.append('involvement', v));
    if (f.points.length === 1) {
      params.set('has_points', f.points[0] === 'with' ? 'true' : 'false');
    }
    const dateFrom = dateFromForPreset(f.datePreset);
    if (dateFrom) params.set('date_from', dateFrom);
    return `${apiUrl}/api/v1/recognitions/feed?${params.toString()}`;
  };

  // The nominations feed endpoint has no filter params; apply the shared
  // sections client-side (feed pages are small).
  const filterNominations = (items, f) => {
    const dateFrom = dateFromForPreset(f.datePreset);
    const userIds = new Set(f.users.map((u) => u.value));
    const myName = (currentUser?.full_name || '').toLowerCase();
    return items.filter((n) => {
      if (userIds.size > 0 && !userIds.has(n.sender?.id) && !userIds.has(n.recipient?.id)) {
        return false;
      }
      if (f.involvement.length > 0) {
        const matches = f.involvement.some((v) => {
          if (v === 'sent') return n.sender?.id === currentUser?.id;
          if (v === 'received') return n.recipient?.id === currentUser?.id;
          if (v === 'mentioned') {
            return myName && (n.message || '').toLowerCase().includes(myName);
          }
          return false;
        });
        if (!matches) return false;
      }
      if (dateFrom && new Date(n.timestamp || n.date || 0) < new Date(dateFrom)) {
        return false;
      }
      return true;
    });
  };

  // Fetch recognitions and nominations from API
  const fetchFeedData = async (sortBy = 'newest', activeFilters = DEFAULT_FILTERS) => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const onlyKind =
        activeFilters.itemKinds.length === 1 ? activeFilters.itemKinds[0] : null;
      const skipRecognitions = onlyKind === 'nomination';
      const skipNominations =
        onlyKind === 'recognition' || hasRecognitionOnlyFilters(activeFilters);

      // Fetch recognitions and nominations in parallel
      const [recognitionsRes, nominationsRes] = await Promise.all([
        skipRecognitions
          ? Promise.resolve(null)
          : fetch(buildRecognitionsFeedUrl(apiUrl, sortBy, activeFilters), { headers }),
        skipNominations
          ? Promise.resolve(null)
          : fetch(`${apiUrl}/api/v1/nominations/feed`, { headers })
      ]);

      let recognitionItems = [];
      if (recognitionsRes) {
        if (!recognitionsRes.ok) {
          throw new Error(`Failed to fetch recognitions: ${recognitionsRes.status}`);
        }
        const recognitionsData = await recognitionsRes.json();
        recognitionItems = (recognitionsData.data || recognitionsData || []).map(r => ({ ...r, item_type: 'recognition' }));
      }
      setRecognitions(recognitionItems);

      // Nominations endpoint might not exist yet, handle gracefully
      let nominationItems = [];
      if (nominationsRes && nominationsRes.ok) {
        const nominationsData = await nominationsRes.json();
        nominationItems = (nominationsData.data || nominationsData || []).map(n => ({ ...n, item_type: 'nomination' }));
        nominationItems = filterNominations(nominationItems, activeFilters);
      }
      setNominations(nominationItems);

      fetchReactions(recognitionItems, nominationItems);
    } catch (err) {
      setError(err.message);
      setRecognitions([]);
      setNominations([]);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  const fetchReactions = async (recognitionItems, nominationItems) => {
    try {
      const [recSummaries, nomSummaries] = await Promise.all([
        reactionsService.getSummary('recognition', recognitionItems.map((r) => r.id)),
        reactionsService.getSummary('nomination', nominationItems.map((n) => n.id)),
      ]);
      const map = {};
      [...recSummaries, ...nomSummaries].forEach((s) => {
        map[`${s.target_type}:${s.target_id}`] = s.reactions;
      });
      setReactionsMap(map);
    } catch (err) {
      // Reactions are non-critical; leave the feed usable without them.
      console.error('Failed to fetch reactions:', err);
    }

    // Comment counts (batched, non-critical) for the card comment toggles.
    try {
      const [recCounts, nomCounts] = await Promise.all([
        commentsService.getCounts('recognition', recognitionItems.map((r) => r.id)),
        commentsService.getCounts('nomination', nominationItems.map((n) => n.id)),
      ]);
      const cmap = {};
      [...recCounts, ...nomCounts].forEach((c) => {
        cmap[`${c.target_type}:${c.target_id}`] = c.count;
      });
      setCommentCountsMap(cmap);
    } catch (err) {
      console.error('Failed to fetch comment counts:', err);
    }
  };

  // Load the teams the current user administers (for delete permissions).
  useEffect(() => {
    teamsService
      .getMyAdminTeamIds()
      .then((ids) => setAdminTeamIds(ids || []))
      .catch(() => setAdminTeamIds([]));
  }, []);

  // Load teams for the filter panel options.
  useEffect(() => {
    teamsService
      .getAllTeams()
      .then((data) => setTeams(data || []))
      .catch(() => setTeams([]));
  }, []);

  useEffect(() => {
    fetchFeedData(sortOrder, filters);
    fetchRecognitionTypes();

    // Listen for new recognitions/nominations
    const handleFeedUpdate = () => {
      fetchFeedData(sortOrder, filters);
    };

    window.addEventListener('recognitionCreated', handleFeedUpdate);
    window.addEventListener('nominationCreated', handleFeedUpdate);

    return () => {
      window.removeEventListener('recognitionCreated', handleFeedUpdate);
      window.removeEventListener('nominationCreated', handleFeedUpdate);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortOrder, filters]);

  // Combine and sort feed items (recognitions + nominations); filtering
  // happens server-side (recognitions) or at fetch time (nominations).
  const getSortedFeedItems = () => {
    const allItems = [...recognitions, ...nominations];

    if (allItems.length === 0) return [];

    const sorted = [...allItems];
    switch (sortOrder) {
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.timestamp || a.date) - new Date(b.timestamp || b.date));
      case 'highest_points': {
        const getPoints = (item) => {
          if (item.item_type === 'nomination') {
            return Number(item.points_awarded_recipient ?? item.campaign?.award_points ?? 0);
          }
          return Number(item.points_amount ?? 0);
        };
        const withPoints = sorted.filter((item) => getPoints(item) > 0);
        const zeroPoints = sorted.filter((item) => getPoints(item) <= 0);
        withPoints.sort((a, b) => getPoints(b) - getPoints(a));
        zeroPoints.sort((a, b) => new Date(b.timestamp || b.date || 0) - new Date(a.timestamp || a.date || 0));
        return [...withPoints, ...zeroPoints];
      }
      default: // newest
        return sorted.sort((a, b) => new Date(b.timestamp || b.date) - new Date(a.timestamp || a.date));
    }
  };

  // Remove deleted recognition (and any batch siblings) from the feed.
  const handleRecognitionDeleted = (deletedIds = []) => {
    const removed = new Set(deletedIds);
    setRecognitions((prev) =>
      prev.filter((r) => !removed.has(r.id))
    );
  };

  const handleNominationDeleted = (nominationId) => {
    setNominations((prev) => prev.filter((n) => n.id !== nominationId));
  };

  const handleNominationUpdated = (updated) => {
    setNominations((prev) =>
      prev.map((n) => (n.id === updated.id ? { ...n, ...updated, item_type: "nomination" } : n))
    );
  };

  const sortedFeedItems = getSortedFeedItems();

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-gray-50 pb-8 pt-3 sm:pt-8 px-4 sm:px-6 lg:px-8">
      <div className="flex gap-8 items-start">
        <div className="flex-1 min-w-0" ref={feedColRef}>
        <div
          className="max-w-4xl space-y-4 lg:space-y-6"
          style={{ marginLeft: feedShift, marginRight: 0 }}
        >
          {/* Banner. The text card always renders: without an admin-set welcome
              title it greets the viewer by name rather than leaving a gap. */}
          <div className="mt-2 mb-4 relative">
            {/* Banner Image - full width */}
            {orgTheme.bannerImageUrl && (
              <div className="h-48 rounded-2xl overflow-hidden shadow-md -mx-2 sm:-mx-4">
                <img src={orgTheme.bannerImageUrl} alt="" className="w-full h-full object-cover" style={{ objectPosition: `center ${orgTheme.bannerPosition}%` }} />
              </div>
            )}

            {/* Welcome Text Card - overlaps bottom of banner by half its height */}
            <div
              className={`px-6 py-5 rounded-2xl shadow-sm ${orgTheme.bannerImageUrl ? '-mt-12 relative z-10 mx-2 sm:mx-4' : ''}`}
              style={{ background: orgTheme.primaryColor }}
            >
              <h2 className="text-white text-2xl font-bold">
                {orgTheme.welcomeTitle || `Hello ${greetingName(currentUser)}`}
              </h2>
              {orgTheme.welcomeMessage && (
                <p className="text-white/80 text-sm mt-1">{orgTheme.welcomeMessage}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between sm:justify-end gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setFilterOpen((v) => !v)}
                aria-expanded={filterOpen}
                className={`flex items-center gap-2 rounded-full pl-3 pr-4 py-2 text-sm border focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors ${
                  activeFilterCount > 0 || filterOpen
                    ? "bg-purple-50 border-purple-300 text-purple-800 hover:bg-purple-100"
                    : "bg-white border-gray-300 hover:bg-gray-50"
                }`}
              >
                <SlidersHorizontal
                  className={`w-4 h-4 ${
                    activeFilterCount > 0 || filterOpen ? "text-purple-600" : "text-gray-500"
                  }`}
                />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full org-bg-primary bg-purple-600 text-white text-[11px] px-1">
                    {activeFilterCount}
                  </span>
                )}
              </button>

              <div className="relative" ref={sortRef}>
                <button
                  type="button"
                  onClick={() => setSortOpen((v) => !v)}
                  className="flex items-center gap-2 bg-white border border-gray-300 rounded-full pl-4 pr-3 py-2 text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors"
                >
                  <span>{sortLabels[sortOrder]}</span>
                  <ArrowUpDown className="w-4 h-4 text-gray-500" />
                </button>

                {sortOpen && (
                  <div className="absolute top-full right-0 mt-2 bg-white border border-gray-200 rounded-2xl shadow-lg overflow-hidden z-20 min-w-[180px] py-1 menu-pop-in">
                    {Object.entries(sortLabels).map(([value, label]) => {
                      const isSelected = value === sortOrder;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => {
                            setSortOrder(value);
                            setSortOpen(false);
                          }}
                          className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-sm transition-colors ${
                            isSelected
                              ? "bg-purple-50 text-purple-700 font-medium"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <span>{label}</span>
                          {isSelected && <Check className="w-4 h-4" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <FeedFilterPanel
              open={filterOpen}
              onClose={() => setFilterOpen(false)}
              isMobile={isMobile}
              filters={filters}
              onChange={setFilters}
              onClear={() => setFilters(DEFAULT_FILTERS)}
              recognitionTypes={recognitionTypesData}
              teams={teams}
              resultCount={sortedFeedItems.length}
              loading={loading}
            />

            {/* Removable summary chips while the panel is closed (desktop);
                on mobile the sheet covers the feed, so chips always show. */}
            {(isMobile || !filterOpen) && (
              <ActiveFilterChips
                filters={filters}
                onChange={setFilters}
                onClear={() => setFilters(DEFAULT_FILTERS)}
                recognitionTypes={recognitionTypesData}
                teams={teams}
              />
            )}
          </div>

          {/* Recognition Cards */}
          {loading && !hasLoadedOnce ? (
            <ResponsiveCard className="text-center">
              <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading recognitions...</p>
            </ResponsiveCard>
          ) : error ? (
            <ResponsiveCard className="text-center">
              <p className="text-red-600 mb-4">Error loading recognitions</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 org-bg-primary org-btn text-white rounded-full transition-colors"
              >
                Retry
              </button>
            </ResponsiveCard>
          ) : sortedFeedItems.length === 0 ? (
            <ResponsiveCard className="text-center">
              <Award className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No activity found</p>
              <p className="text-gray-500 text-sm">Be the first to give recognition or make a nomination!</p>
            </ResponsiveCard>
          ) : (
            <div className={`space-y-4 lg:space-y-6 transition-opacity duration-200 ${loading ? 'opacity-60 pointer-events-none' : ''}`}>
              {sortedFeedItems.map((item, index) => (
                <motion.div
                  key={item.item_type === 'nomination' ? `nom-${item.id}` : `rec-${item.id}`}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: 'easeOut', delay: Math.min(index * 0.04, 0.28) }}
                >
                  {item.item_type === 'nomination'
                    ? <NominationCard data={item} reactions={reactionsMap[`nomination:${item.id}`]} commentCount={commentCountsMap[`nomination:${item.id}`] || 0} onDeleted={handleNominationDeleted} onUpdated={handleNominationUpdated} />
                    : <EnhancedRecognitionCard data={item} withImage={!!item.image_url} typeConfig={recognitionTypeConfig} adminTeamIds={adminTeamIds} onDeleted={handleRecognitionDeleted} reactions={reactionsMap[`recognition:${item.id}`]} commentCount={commentCountsMap[`recognition:${item.id}`] || 0} />}
                </motion.div>
              ))}
            </div>
          )}
        </div>
        </div>
        <ImportantDatesRail />
      </div>
    </div>
    </MotionConfig>
  );
}


const htmlToPlainText = (html) => {
  if (!html) return "";
  if (typeof document === "undefined") {
    return String(html)
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\n{3,}/g, "\n\n")
      .trim();
  }
  const el = document.createElement("div");
  el.innerHTML = html;
  return (el.innerText || el.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
};

const CardActionsMenu = ({
  visibilityLabel,
  VisibilityIcon,
  showVisibility = true,
  actions = [],
  canDelete = false,
  onDelete,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return undefined;
    const place = () => {
      const menu = menuRef.current;
      const btn = btnRef.current;
      if (!menu || !btn) return;
      const a = btn.getBoundingClientRect();
      const margin = 8;
      const w = menu.offsetWidth || 200;
      const h = menu.offsetHeight || 120;
      let left = a.right - w;
      left = Math.min(Math.max(left, margin), window.innerWidth - w - margin);
      let top = a.bottom + 6;
      if (top + h > window.innerHeight - margin) {
        top = Math.max(margin, a.top - h - 6);
      }
      setPos({ left, top });
    };
    place();
    const onScroll = (e) => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const handle = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    document.addEventListener("touchstart", handle);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handle);
      document.removeEventListener("touchstart", handle);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!showVisibility && actions.length === 0 && !canDelete) return null;

  return (
    <>
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center text-xs p-1.5 rounded-full text-white/90 bg-white/15 hover:bg-white/25 cursor-pointer"
        title="More"
        aria-label="Card options"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <MoreVertical className="w-3.5 h-3.5" />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[210] w-52 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden menu-pop-in"
            style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: "hidden" }}
          >
            {showVisibility && (
              <div
                role="menuitem"
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-800 border-b border-gray-100"
              >
                {VisibilityIcon && <VisibilityIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />}
                <div className="min-w-0">
                  <p className="font-medium truncate">{visibilityLabel || "Everyone"}</p>
                  <p className="text-[11px] text-gray-400">Visibility</p>
                </div>
              </div>
            )}
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  action.onClick && action.onClick();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {action.Icon ? <action.Icon className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                {action.label}
              </button>
            ))}
            {canDelete && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onDelete && onDelete();
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </div>,
          document.body
        )}
    </>
  );
};

// EnhancedRecognitionCard - reactions (ReactionBar) and comments (CommentSection) enabled
const EnhancedRecognitionCard = ({ withImage = false, data, typeConfig = DEFAULT_TYPE_CONFIG, adminTeamIds = [], onDeleted, reactions, commentCount = 0 }) => {
  const { recognitionColor } = useContext(OrgThemeContext);
  const { user: currentUser } = useContext(AuthContext);
  // Map API fields to component fields (handle both old and new API formats)
  const sender = data.sender || data.recognized_by || {};
  const recipient = data.recipient || data.recognized_user || {};
  const allRecipients = [recipient, ...(data.co_recipients || [])].filter((r) => r && r.id);
  const recipientCount = allRecipients.length;
  const isMultiRecipient = recipientCount > MAX_VISIBLE_RECIPIENTS;
  const points = data.points_amount || data.points || 0;
  const timestamp = data.timestamp || data.date;

  const [visibility, setVisibility] = React.useState(data.visibility || "org");
  const [visibleTeamId, setVisibleTeamId] = React.useState(data.visible_team_id || null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editMessageOpen, setEditMessageOpen] = React.useState(false);
  const [message, setMessage] = React.useState(data.message || "");
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [peopleMenuOpen, setPeopleMenuOpen] = React.useState(false);
  const peopleMenuBtnRef = useRef(null);
  const [workvivoInfo, setWorkvivoInfo] = React.useState(data.workvivo || null);
  const [workvivoBusy, setWorkvivoBusy] = React.useState(false);
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const [liveCommentCount, setLiveCommentCount] = React.useState(commentCount);
  const cardRootRef = useRef(null);
  React.useEffect(() => setLiveCommentCount(commentCount), [commentCount]);
  const { closeEditorsToken, onEditorActiveChange } = useCommentPanelDismiss(
    cardRootRef,
    commentsOpen,
    setCommentsOpen
  );

  const canEditVisibility =
    currentUser &&
    (currentUser.id === sender.id ||
      currentUser.is_superuser ||
      currentUser.role === "admin");
  const canEditMessage = canEditVisibility;
  const canManageWorkvivo = canEditVisibility;
  // Sender / org admins / superusers, plus team admins of the recognition's
  // visibility or budget team. The backend re-checks all of this on delete.
  const isTeamAdminForCard =
    (adminTeamIds || []).length > 0 &&
    [data.visible_team_id, data.team_id]
      .filter(Boolean)
      .some((tid) => adminTeamIds.includes(tid));
  const canDelete =
    currentUser &&
    (currentUser.id === sender.id ||
      currentUser.is_superuser ||
      currentUser.role === "admin" ||
      isTeamAdminForCard);
  const visMeta = VISIBILITY_META[visibility] || VISIBILITY_META.org;
  const VisIcon = visMeta.Icon;

  // Lazy-load Workvivo status if missing (e.g. for items already in feed)
  React.useEffect(() => {
    if (workvivoInfo !== null && workvivoInfo !== undefined) return;
    let cancelled = false;
    workvivoService
      .getRecognitionStatus(data.id)
      .then((info) => {
        if (cancelled) return;
        if (info && info.status && info.status !== "not_posted") {
          setWorkvivoInfo(info);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.id]);

  const handleRetryWorkvivo = async () => {
    if (workvivoBusy) return;
    setWorkvivoBusy(true);
    try {
      const info = await workvivoService.retryRecognition(data.id);
      setWorkvivoInfo(info);
      if (info?.status === "posted") {
        // success — no alert
      } else {
        alert(`Retry status: ${info?.status}\n${info?.last_error || ""}`);
      }
    } catch (e) {
      alert(`Failed to retry Workvivo post: ${e.message || e}`);
    } finally {
      setWorkvivoBusy(false);
    }
  };

  // Recognition type ring highlight
  const typeColor = data.recognition_type?.color_from
    ? (extractHexColor(data.recognition_type.color_from) || data.recognition_type.color_from)
    : recognitionColor;

  const typeColorTo = data.recognition_type?.color_to
    ? (extractHexColor(data.recognition_type.color_to) || data.recognition_type.color_to)
    : typeColor;

  const timeParts = formatRelativeTimestampParts(timestamp);
  const senderName = sender.full_name || sender.name || sender.email || "Unknown User";

  const recognitionTypeLabel = data.recognition_type ? (
    <span className="inline-flex items-center gap-1 font-medium" style={{ color: typeColor }}>
      {data.recognition_type.emoji && data.recognition_type.emoji.startsWith("/api/v1/recognition-type-icons/") ? (
        <img src={data.recognition_type.emoji} alt="" className="w-4 h-4 object-contain" />
      ) : (
        <span aria-hidden="true">{data.recognition_type.emoji || "🏅"}</span>
      )}
      <span>{data.occasion_label || data.recognition_type.name}</span>
    </span>
  ) : null;

  return (
    <div className="relative" ref={cardRootRef}>
      {editOpen && (
        <VisibilityEditModal
          recognitionId={data.id}
          initialVisibility={visibility}
          initialTeamId={visibleTeamId}
          accentColor={typeColor}
          accentColorTo={typeColorTo}
          onClose={() => setEditOpen(false)}
          onSaved={(updated) => {
            setVisibility(updated.visibility);
            setVisibleTeamId(updated.visible_team_id || null);
            setEditOpen(false);
          }}
        />
      )}

      {editMessageOpen && (
        <EditMessageModal
          title="Edit recognition"
          initialMessage={message}
          accentColor={typeColor}
          accentColorTo={typeColorTo}
          onClose={() => setEditMessageOpen(false)}
          onSave={async (plain) => {
            const updated = await recognitionsService.updateMessage(data.id, plain);
            const next = updated?.data?.message ?? updated?.message ?? plain;
            setMessage(next);
            setEditMessageOpen(false);
          }}
        />
      )}

      {deleteOpen && (
        <DeleteRecognitionModal
          recognitionId={data.id}
          points={points}
          recipientNames={allRecipients.map((r) => recipientDisplayName(r))}
          accentColor={typeColor}
          accentColorTo={typeColorTo}
          onClose={() => setDeleteOpen(false)}
          onDeleted={(result) => {
            setDeleteOpen(false);
            onDeleted && onDeleted(result?.deleted_ids || [data.id]);
          }}
        />
      )}

      {peopleMenuOpen && (
        <RecipientOverflowMenu
          people={allRecipients}
          anchorEl={peopleMenuBtnRef.current}
          onClose={() => setPeopleMenuOpen(false)}
          typeColor={typeColor}
          title="Recognized people"
        />
      )}

      <div className="relative pb-4">
      <div
        data-feed-card
        className="bg-white rounded-2xl overflow-hidden border-2
                   transition-all duration-300 ease-out
                   [border-color:var(--card-border-default)]
                   [box-shadow:0_4px_6px_-1px_rgba(0,0,0,0.07),0_2px_4px_-2px_rgba(0,0,0,0.06)]
                   [@media(hover:hover)]:hover:-translate-y-1
                   [@media(hover:hover)]:hover:[border-color:var(--card-hex)]
                   [@media(hover:hover)]:hover:[box-shadow:0_25px_50px_-12px_var(--card-shadow),0_10px_20px_-10px_rgba(0,0,0,0.15)]
                   [&.card-hover-hold]:-translate-y-1
                   [&.card-hover-hold]:[border-color:var(--card-hex)]
                   [&.card-hover-hold]:[box-shadow:0_25px_50px_-12px_var(--card-shadow),0_10px_20px_-10px_rgba(0,0,0,0.15)]"
        style={{
          '--card-hex': typeColor,
          '--card-border-default': `${typeColor}80`,
          '--card-shadow': `${typeColor}66`,
        }}
      >
        <div
          className="px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2"
          style={{ background: `linear-gradient(to right, ${typeColor}, ${typeColorTo})` }}
        >
          <div className="flex items-center text-[11px] sm:text-xs text-white min-w-0">
            <Award className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mr-1.5" />
            <span className="font-semibold uppercase tracking-wider">Recognition</span>
            {timeParts && (
              <>
                <HeaderMetaSep />
                <span className="text-white/90 whitespace-nowrap">{timeParts.dayLabel}</span>
                <HeaderMetaSep />
                <span
                  className="text-white/90 whitespace-nowrap"
                  title={timestamp ? new Date(timestamp).toLocaleString() : undefined}
                >
                  {timeParts.time}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <CardActionsMenu
              visibilityLabel={visMeta.label}
              VisibilityIcon={VisIcon}
              showVisibility
              canDelete={canDelete}
              onDelete={() => setDeleteOpen(true)}
              actions={[
                ...(canEditMessage
                  ? [{ label: "Edit message", Icon: Pencil, onClick: () => setEditMessageOpen(true) }]
                  : []),
                ...(canEditVisibility
                  ? [{ label: "Edit visibility", Icon: Eye, onClick: () => setEditOpen(true) }]
                  : []),
              ]}
            />
          </div>
        </div>

        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-7 sm:pb-8 flex flex-col items-stretch sm:items-center sm:text-center">
          <div className="flex flex-row sm:flex-col items-center sm:items-center gap-3 sm:gap-0 w-full">
            {data.is_team_wide && data.team_name ? (
              <div className="min-w-0 flex-1 sm:flex-none text-left sm:text-center w-full">
                <div className="text-base sm:text-xl font-bold text-gray-900 leading-snug">
                  <Link to="/teams" className="hover:underline">
                    {data.team_name}
                  </Link>
                  <span className="font-normal text-gray-600">{" recognized by "}</span>
                  <Link to={`/profile/${sender.id}`} className="hover:underline">
                    {senderName}
                  </Link>
                </div>
                {data.recognition_type && (
                  <div className="mt-0.5 sm:mt-1 flex items-center justify-start sm:justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-500 flex-wrap">
                    <span>for</span>
                    {recognitionTypeLabel}
                  </div>
                )}
              </div>
            ) : isMultiRecipient ? (
              <div className="min-w-0 flex-1 sm:flex-none text-left sm:text-center w-full">
                <div className="text-base sm:text-xl font-bold text-gray-900 leading-snug">
                  <button
                    type="button"
                    ref={peopleMenuBtnRef}
                    onClick={() => setPeopleMenuOpen(true)}
                    className="font-bold hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 rounded-sm"
                    style={{ color: typeColor }}
                    aria-label={`Show ${recipientCount} recognized people`}
                  >
                    {recipientCount} people
                  </button>
                  <span className="font-normal text-gray-600">{" recognized by "}</span>
                  <Link to={`/profile/${sender.id}`} className="hover:underline">
                    {senderName}
                  </Link>
                </div>
                {data.recognition_type && (
                  <div className="mt-0.5 sm:mt-1 flex items-center justify-start sm:justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-500 flex-wrap">
                    <span>for</span>
                    {recognitionTypeLabel}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex-shrink-0 sm:mb-4">
                  <RecipientAvatarCluster recipients={allRecipients} typeColor={typeColor} />
                </div>

                <div className="min-w-0 flex-1 sm:flex-none text-left sm:text-center">
                  <div className="text-base sm:text-xl font-bold text-gray-900 leading-snug">
                    <RecipientNames recipients={allRecipients} typeColor={typeColor} />
                    <span className="font-normal text-gray-600">{" recognized by "}</span>
                    <Link to={`/profile/${sender.id}`} className="hover:underline">
                      {senderName}
                    </Link>
                  </div>
                  {data.recognition_type && (
                    <div className="mt-0.5 sm:mt-1 flex items-center justify-start sm:justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-500 flex-wrap">
                      <span>for</span>
                      {recognitionTypeLabel}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {message && (
            <div className="mt-3 sm:mt-5 text-sm sm:text-base text-gray-700 leading-relaxed max-w-prose w-full text-left whitespace-pre-wrap">
              {htmlToPlainText(message)}
            </div>
          )}

          {withImage && data.image_url && (
            <div className="mt-3 sm:mt-4 w-full">
              <img
                src={data.image_url}
                alt="Recognition"
                className="w-full h-48 sm:h-64 object-cover rounded-2xl"
              />
            </div>
          )}

          {workvivoInfo && workvivoInfo.status === "posted" && (
            <div className="mt-4 flex items-center gap-2 text-xs">
              <a
                href={workvivoInfo.workvivo_permalink || "#"}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                title="Open kudo on Workvivo"
              >
                <ExternalLink className="w-3 h-3" />
                Posted to Workvivo
              </a>
            </div>
          )}
          {workvivoInfo && workvivoInfo.status === "failed" && canManageWorkvivo && (
            <div className="mt-4 flex items-center gap-2 text-xs">
              <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-50 text-red-700">
                Workvivo post failed
              </span>
              <button
                type="button"
                onClick={handleRetryWorkvivo}
                disabled={workvivoBusy}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${workvivoBusy ? "animate-spin" : ""}`} />
                Retry
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="absolute inset-x-3 sm:inset-x-5 bottom-4 z-20 translate-y-1/2 flex flex-nowrap items-center justify-between gap-2 pointer-events-none">
        <div className="min-w-0 flex-1 overflow-hidden pointer-events-auto">
          <ReactionBar
            targetType="recognition"
            targetId={data.id}
            initialReactions={reactions}
            accentColor={typeColor}
          />
        </div>
        {/* Points ride with the comment toggle on the right so the reaction pills
            growing/shrinking on the left never shift them. */}
        <div className="flex-shrink-0 flex flex-nowrap items-center gap-1.5 sm:gap-2 pointer-events-auto">
          {points > 0 && (
            <span className="inline-flex flex-shrink-0 items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full bg-gray-900 text-white text-xs sm:text-sm font-medium shadow-md">
              <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 fill-yellow-400 text-yellow-400" />
              +{points}<span className="hidden sm:inline">&nbsp;points</span>
              {data.points_hidden && (
                <span className="ml-1 text-[10px] sm:text-xs font-normal text-gray-300">· hidden</span>
              )}
            </span>
          )}
          <CommentToggle
            count={liveCommentCount}
            open={commentsOpen}
            onClick={() => setCommentsOpen((o) => !o)}
            accentColor={typeColor}
          />
        </div>
      </div>
      </div>

      <div className="px-2 sm:px-4">
        <CommentSection
          targetType="recognition"
          targetId={data.id}
          accentColor={typeColor}
          initialCount={commentCount}
          open={commentsOpen}
          onCountChange={setLiveCommentCount}
          onEditorActiveChange={onEditorActiveChange}
          closeEditorsToken={closeEditorsToken}
        />
      </div>
    </div>
  );
};


// Modal for editing visibility on an existing recognition
const VisibilityEditModal = ({ recognitionId, initialVisibility, initialTeamId, accentColor = "#111827", accentColorTo, onClose, onSaved }) => {
  // Single accent if the card has no color_to
  const accentTo = accentColorTo || accentColor;
  const [visibility, setVisibility] = React.useState(initialVisibility || "org");
  const [teams, setTeams] = React.useState([]);
  const [selectedTeam, setSelectedTeam] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    teamsService.getMyTeams().then((res) => {
      const opts = (res || []).map((t) => ({
        value: t.id || t.team_id,
        label: t.name || t.team_name || (t.id || t.team_id),
      }));
      setTeams(opts);
      if (initialTeamId) {
        const match = opts.find((o) => o.value === initialTeamId);
        if (match) setSelectedTeam(match);
      }
    }).catch(() => setTeams([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (visibility === "team" && !selectedTeam) {
      setError("Please select a team.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await recognitionsService.updateVisibility(recognitionId, {
        visibility,
        visible_team_id: visibility === "team" ? selectedTeam.value : null,
      });
      onSaved(updated);
    } catch (e) {
      setError(e.message || "Failed to update visibility");
    } finally {
      setSaving(false);
    }
  };

  // Horizontal option chips for visibility levels
  const options = [
    { value: "org",     icon: Globe, label: "Everyone" },
    { value: "team",    icon: Users, label: "Team" },
    { value: "private", icon: Lock,  label: "Private" },
  ];

  return (
    // Simple dim backdrop on edits.
    <div
      className="absolute inset-0 bg-black/30 flex items-center justify-center z-30 p-3 rounded-2xl menu-backdrop-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl overflow-hidden w-full max-w-[420px] relative border-2 menu-pop-in"
        style={{
          borderColor: `${accentColor}80`,
          boxShadow: `0 25px 50px -12px ${accentColor}55, 0 10px 20px -10px rgba(0,0,0,0.18)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Thin colored header */}
        <div
          className="h-1.5 w-full"
          style={{ background: `linear-gradient(to right, ${accentColor}, ${accentTo})` }}
        />

        <div className="p-5">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close">
            <span className="text-base leading-none">✕</span>
          </button>

          <h3 className="text-base font-semibold text-gray-900">Edit visibility</h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">Choose who can see this recognition.</p>

          {/* 3-up horizontal chips */}
          <div className="grid grid-cols-3 gap-2">
            {options.map(({ value, icon: OptionIcon, label }) => {
              const isSelected = visibility === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setVisibility(value)}
                  className="flex flex-col items-center justify-center gap-1.5 px-2 py-3 rounded-xl border text-center transition-all"
                  style={
                    isSelected
                      ? { background: accentColor, color: "#ffffff", borderColor: accentColor }
                      : { background: "#ffffff", color: "#374151", borderColor: "#e5e7eb" }
                  }
                >
                  <OptionIcon
                    className="w-5 h-5"
                    style={{ color: isSelected ? "#ffffff" : accentColor }}
                  />
                  <div className="font-medium text-sm">{label}</div>
                </button>
              );
            })}
          </div>

          {visibility === "team" && (
            <Select
              options={teams}
              value={selectedTeam}
              onChange={setSelectedTeam}
              className="mt-3"
              placeholder="Select a team..."
              noOptionsMessage={() => "No teams available"}
              menuPlacement="auto"
              menuPosition="fixed"
            />
          )}

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          <div className="mt-4 flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 font-medium transition-opacity"
              style={{ background: accentColor }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


// Modal to confirm deleting a recognition (with optional points reversal)
const DeleteRecognitionModal = ({ recognitionId, points = 0, recipientNames = [], accentColor = "#dc2626", accentColorTo, onClose, onDeleted }) => {
  const accentTo = accentColorTo || accentColor;
  const hasPoints = Number(points) > 0;
  // When points were awarded, let the actor choose whether to claw them back.
  const [reversePoints, setReversePoints] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const recipientLabel =
    recipientNames.length === 0
      ? "this recognition"
      : recipientNames.length === 1
        ? `the recognition for ${recipientNames[0]}`
        : `the recognition for ${recipientNames.length} people`;

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      const result = await recognitionsService.deleteRecognition(recognitionId, {
        reversePoints: hasPoints ? reversePoints : false,
      });
      onDeleted(result);
    } catch (e) {
      setError(e.message || "Failed to delete recognition");
      setDeleting(false);
    }
  };

  return (
    <div
      className="absolute inset-0 bg-black/30 flex items-center justify-center z-30 p-3 rounded-2xl menu-backdrop-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl overflow-hidden w-full max-w-[440px] relative border-2 menu-pop-in"
        style={{
          borderColor: `${accentColor}80`,
          boxShadow: `0 25px 50px -12px ${accentColor}55, 0 10px 20px -10px rgba(0,0,0,0.18)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1.5 w-full"
          style={{ background: `linear-gradient(to right, ${accentColor}, ${accentTo})` }}
        />

        <div className="p-5">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close">
            <span className="text-base leading-none">✕</span>
          </button>

          <h3 className="text-base font-semibold text-gray-900">Delete recognition?</h3>
          <p className="text-sm text-gray-600 mt-1">
            This will permanently remove {recipientLabel}. This can't be undone.
          </p>

          {hasPoints && (
            <div className="mt-4 space-y-2">
              <label
                className="flex items-start gap-2 p-3 rounded-xl border cursor-pointer transition-all"
                style={!reversePoints ? { borderColor: accentColor, background: `${accentColor}0d` } : { borderColor: "#e5e7eb" }}
              >
                <input
                  type="radio"
                  name="reversePoints"
                  checked={!reversePoints}
                  onChange={() => setReversePoints(false)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900">Keep points</span>
                  <span className="block text-gray-500">Recipients keep the {points} points they received.</span>
                </span>
              </label>
              <label
                className="flex items-start gap-2 p-3 rounded-xl border cursor-pointer transition-all"
                style={reversePoints ? { borderColor: accentColor, background: `${accentColor}0d` } : { borderColor: "#e5e7eb" }}
              >
                <input
                  type="radio"
                  name="reversePoints"
                  checked={reversePoints}
                  onChange={() => setReversePoints(true)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  <span className="font-medium text-gray-900">Reverse points</span>
                  <span className="block text-gray-500">Claw back the {points} points and refund the team budget.</span>
                </span>
              </label>
            </div>
          )}

          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}

          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={onClose}
              disabled={deleting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 font-medium transition-opacity inline-flex items-center gap-1.5"
              style={{ background: "#dc2626" }}
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};


const NominationCard = ({ data, reactions, commentCount = 0, onDeleted, onUpdated }) => {
  const { nominationColor } = useContext(OrgThemeContext);
  const { user: currentUser } = useContext(AuthContext);
  const sender = data.sender || {};
  const recipient = data.recipient || {};
  const campaign = data.campaign || {};
  const award = data.award || null;
  const timestamp = data.timestamp;
  const pointsAwarded = data.points_awarded_recipient || 0;

  const [message, setMessage] = React.useState(data.message || "");
  const [editOpen, setEditOpen] = React.useState(false);
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [commentsOpen, setCommentsOpen] = React.useState(false);
  const [liveCommentCount, setLiveCommentCount] = React.useState(commentCount);
  const cardRootRef = useRef(null);
  React.useEffect(() => setLiveCommentCount(commentCount), [commentCount]);
  const { closeEditorsToken, onEditorActiveChange } = useCommentPanelDismiss(
    cardRootRef,
    commentsOpen,
    setCommentsOpen
  );

  const awardColor = award?.color_from
    ? (extractHexColor(award.color_from) || award.color_from)
    : nominationColor;
  const awardColorTo = award?.color_to
    ? (extractHexColor(award.color_to) || award.color_to)
    : awardColor;

  const senderName = sender.full_name || sender.name || sender.email || "Unknown User";
  const recipientName = recipient.full_name || recipient.name || recipient.email || "Unknown User";

  const canManage =
    currentUser &&
    (currentUser.id === sender.id ||
      currentUser.is_superuser ||
      currentUser.role === "admin");

  const timeParts = formatRelativeTimestampParts(timestamp);

  const awardLabel = award ? (
    <span className="inline-flex items-center gap-1 font-medium" style={{ color: awardColor }}>
      {award.emoji && award.emoji.startsWith("/api/v1/recognition-type-icons/") ? (
        <img src={award.emoji} alt="" className="w-4 h-4 object-contain" />
      ) : (
        <span aria-hidden="true">{award.emoji || "🏆"}</span>
      )}
      <span>{award.name}</span>
    </span>
  ) : null;

  return (
    <div className="relative" ref={cardRootRef}>
      <div className="relative pb-4">
      {editOpen && (
        <EditMessageModal
          title="Edit nomination"
          initialMessage={message}
          accentColor={awardColor}
          accentColorTo={awardColorTo}
          onClose={() => setEditOpen(false)}
          onSave={async (plain) => {
            const updated = await nominationsService.updateNomination(data.id, {
              message: plain,
            });
            const next =
              updated?.data?.message ?? updated?.message ?? plain;
            setMessage(next);
            setEditOpen(false);
            onUpdated && onUpdated({ ...data, message: next });
          }}
        />
      )}
      {deleteOpen && (
        <DeleteNominationModal
          nominationId={data.id}
          recipientName={recipientName}
          accentColor={awardColor}
          accentColorTo={awardColorTo}
          onClose={() => setDeleteOpen(false)}
          onDeleted={() => {
            setDeleteOpen(false);
            onDeleted && onDeleted(data.id);
          }}
        />
      )}
      <div
        data-feed-card
        className="bg-white rounded-2xl overflow-hidden border-2
                   transition-all duration-300 ease-out
                   [border-color:var(--card-border-default)]
                   [box-shadow:0_4px_6px_-1px_rgba(0,0,0,0.07),0_2px_4px_-2px_rgba(0,0,0,0.06)]
                   [@media(hover:hover)]:hover:-translate-y-1
                   [@media(hover:hover)]:hover:[border-color:var(--card-hex)]
                   [@media(hover:hover)]:hover:[box-shadow:0_25px_50px_-12px_var(--card-shadow),0_10px_20px_-10px_rgba(0,0,0,0.15)]
                   [&.card-hover-hold]:-translate-y-1
                   [&.card-hover-hold]:[border-color:var(--card-hex)]
                   [&.card-hover-hold]:[box-shadow:0_25px_50px_-12px_var(--card-shadow),0_10px_20px_-10px_rgba(0,0,0,0.15)]"
        style={{
          '--card-hex': awardColor,
          '--card-border-default': `${awardColor}80`,
          '--card-shadow': `${awardColor}66`,
        }}
      >
        <div
          className="px-3 sm:px-6 py-2 sm:py-3 flex items-center justify-between gap-2"
          style={{ background: `linear-gradient(to right, ${awardColor}, ${awardColorTo})` }}
        >
          <div className="flex items-center text-[11px] sm:text-xs text-white min-w-0">
            <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0 mr-1.5" />
            <span className="font-semibold uppercase tracking-wider">Nomination</span>
            {timeParts && (
              <>
                <HeaderMetaSep />
                <span className="text-white/90 whitespace-nowrap">{timeParts.dayLabel}</span>
                <HeaderMetaSep />
                <span
                  className="text-white/90 whitespace-nowrap"
                  title={timestamp ? new Date(timestamp).toLocaleString() : undefined}
                >
                  {timeParts.time}
                </span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <CardActionsMenu
              showVisibility={false}
              canDelete={!!canManage}
              onDelete={() => setDeleteOpen(true)}
              actions={
                canManage
                  ? [{ label: "Edit message", Icon: Pencil, onClick: () => setEditOpen(true) }]
                  : []
              }
            />
          </div>
        </div>

        <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-7 sm:pb-8 flex flex-col items-stretch sm:items-center sm:text-center">
          <div className="flex flex-row sm:flex-col items-center gap-3 sm:gap-0 w-full">
            <Link
              to={`/profile/${recipient.id}`}
              className="group block flex-shrink-0 sm:mb-4 transition-transform duration-200 hover:scale-105"
            >
              <UserAvatar
                src={recipient.avatar_url}
                name={recipientName}
                className="w-14 h-14 sm:w-24 sm:h-24 rounded-full object-cover transition-shadow duration-200 shadow-md group-hover:shadow-2xl"
                style={{ border: `3px solid ${awardColor}` }}
                textClassName="text-lg sm:text-2xl"
              />
            </Link>

            <div className="min-w-0 flex-1 sm:flex-none text-left sm:text-center">
              <div className="text-base sm:text-xl font-bold text-gray-900 leading-snug">
                <Link to={`/profile/${sender.id}`} className="hover:underline">
                  {senderName}
                </Link>
                <span className="font-normal text-gray-600">{" nominated "}</span>
                <Link to={`/profile/${recipient.id}`} className="hover:underline">
                  {recipientName}
                </Link>
              </div>
              {award && (
                <div className="mt-0.5 sm:mt-1 flex items-center justify-start sm:justify-center gap-1 sm:gap-1.5 text-xs sm:text-sm text-gray-500 flex-wrap">
                  <span>for</span>
                  {awardLabel}
                </div>
              )}
            </div>
          </div>

          {message && (
            <div className="mt-3 sm:mt-5 text-sm sm:text-base text-gray-700 leading-relaxed max-w-prose w-full text-left whitespace-pre-wrap">
              {htmlToPlainText(message)}
            </div>
          )}
        </div>
      </div>

      <div className="absolute inset-x-3 sm:inset-x-5 bottom-4 z-20 translate-y-1/2 flex flex-nowrap items-center justify-between gap-2 pointer-events-none">
        <div className="min-w-0 flex-1 overflow-hidden pointer-events-auto">
          <ReactionBar
            targetType="nomination"
            targetId={data.id}
            initialReactions={reactions}
            accentColor={awardColor}
          />
        </div>
        {/* Campaign title + points ride with the comment toggle on the right so
            the reaction pills growing/shrinking on the left never shift them. */}
        <div className="flex-shrink-0 flex flex-nowrap items-center gap-1.5 pointer-events-auto">
          {campaign?.name && (
            <span
              className="max-w-[7rem] sm:max-w-[11rem] px-2.5 py-1 rounded-full bg-white border-2 text-[11px] sm:text-xs font-medium shadow-md truncate"
              style={{ borderColor: awardColor, color: awardColor }}
              title={campaign.name}
            >
              {campaign.name}
            </span>
          )}
          {pointsAwarded > 0 && (
            <span className="inline-flex flex-shrink-0 items-center gap-1 px-2 py-1 rounded-full bg-gray-900 text-white text-[11px] sm:text-xs font-medium shadow-md">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
              +{pointsAwarded}
            </span>
          )}
          <CommentToggle
            count={liveCommentCount}
            open={commentsOpen}
            onClick={() => setCommentsOpen((o) => !o)}
            accentColor={awardColor}
          />
        </div>
      </div>
      </div>

      <div className="px-2 sm:px-4">
        <CommentSection
          targetType="nomination"
          targetId={data.id}
          accentColor={awardColor}
          initialCount={commentCount}
          open={commentsOpen}
          onCountChange={setLiveCommentCount}
          onEditorActiveChange={onEditorActiveChange}
          closeEditorsToken={closeEditorsToken}
        />
      </div>
    </div>
  );
};

const EditMessageModal = ({
  title = "Edit message",
  initialMessage = "",
  accentColor = "#f97316",
  accentColorTo,
  onClose,
  onSave,
}) => {
  const accentTo = accentColorTo || accentColor;
  const [message, setMessage] = React.useState(() => htmlToPlainText(initialMessage || ""));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleSave = async () => {
    const trimmed = message.trim();
    if (!trimmed) {
      setError("Message is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(trimmed);
    } catch (e) {
      setError(e.message || "Failed to update message");
      setSaving(false);
    }
  };

  return (
    <div
      className="absolute inset-0 bg-black/30 flex items-center justify-center z-30 p-3 rounded-2xl menu-backdrop-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl overflow-hidden w-full max-w-[440px] relative border-2 menu-pop-in"
        style={{
          borderColor: `${accentColor}80`,
          boxShadow: `0 25px 50px -12px ${accentColor}55, 0 10px 20px -10px rgba(0,0,0,0.18)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1.5 w-full"
          style={{ background: `linear-gradient(to right, ${accentColor}, ${accentTo})` }}
        />
        <div className="p-5">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close">
            <span className="text-base leading-none">✕</span>
          </button>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500 mt-0.5 mb-3">Update the message text.</p>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
            maxLength={4000}
          />
          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 font-medium transition-opacity"
              style={{ background: accentColor }}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteNominationModal = ({
  nominationId,
  recipientName = "this person",
  accentColor = "#dc2626",
  accentColorTo,
  onClose,
  onDeleted,
}) => {
  const accentTo = accentColorTo || accentColor;
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await nominationsService.deleteNomination(nominationId);
      onDeleted();
    } catch (e) {
      setError(e.message || "Failed to delete nomination");
      setDeleting(false);
    }
  };

  return (
    <div
      className="absolute inset-0 bg-black/30 flex items-center justify-center z-30 p-3 rounded-2xl menu-backdrop-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl overflow-hidden w-full max-w-[440px] relative border-2 menu-pop-in"
        style={{
          borderColor: `${accentColor}80`,
          boxShadow: `0 25px 50px -12px ${accentColor}55, 0 10px 20px -10px rgba(0,0,0,0.18)`,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="h-1.5 w-full"
          style={{ background: `linear-gradient(to right, ${accentColor}, ${accentTo})` }}
        />
        <div className="p-5">
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" aria-label="Close">
            <span className="text-base leading-none">✕</span>
          </button>
          <h3 className="text-base font-semibold text-gray-900">Delete nomination?</h3>
          <p className="text-sm text-gray-600 mt-1">
            This will permanently remove the nomination for {recipientName}. This can&apos;t be undone.
          </p>
          {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 font-medium transition-opacity inline-flex items-center gap-1.5"
              style={{ background: "#dc2626" }}
            >
              <Trash2 className="w-4 h-4" />
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { EnhancedRecognitionCard, NominationCard };